/**
 * Presenting `GET /api/admin/tenants/{id}/demo-connections`. Pure — no React,
 * no fetching.
 *
 * THE CONTRACT IS THE AUTHORITY. Every value this module branches on appears in
 * `demoConnectionsContract.json` beside this file, which is generated from
 * `backend/modules/admin/services/demo_profile_intent.py::CONTRACT` and pinned
 * by a backend test. `demoConnectionsContract.test.js` asserts that the maps
 * below cover those domains exactly — no value missing, and no value invented.
 *
 * That test is not ceremony. The shared Connection Picker shipped a health
 * vocabulary (`degraded`/`ok`/`passing`) that the product never wrote, with a
 * green suite the whole way, because its fixture and its renderer agreed with
 * each other and neither agreed with the server. A value domain moving under a
 * stable field name is invisible to any test that owns both sides.
 *
 * THREE CONCEPTS, KEPT APART
 * ==========================
 *   DESIRED    what the operator approved, frozen at approval.
 *   ATTEMPTED  what provisioning tried, at attempt level only.
 *   ACTUAL     what the runtime reports NOW, read live and never stored.
 *
 * Nothing in the ACTUAL half of this module reads the DESIRED half. The whole
 * defect this programme exists to close was a screen that rendered five
 * connection names under "Will connect automatically" — DESIRED presented as
 * though it had already succeeded — and it said five whether the profile held
 * five, held nothing, or could not be reached.
 *
 * FOUR RULES THAT ARE EASY TO BREAK AND EXPENSIVE TO BREAK
 * =======================================================
 *  1. `actual.state === 'unavailable'` means every count is `null`. A `0` on
 *     that screen states a measurement nobody made. `formatCount` is the only
 *     way a count reaches the render, and it cannot turn `null` into `0`.
 *  2. `unknown` means NOT ESTABLISHED, not failed. It never renders in a
 *     destructive tone. Neither do `unrecognised` or `no_outcome_reported`:
 *     none of the three is a measurement of failure.
 *  3. `profile_drift: null` is NOT "no drift" — it is "not comparable".
 *  4. `desired.state === 'unverified'` means the frozen specification failed
 *     its tamper seal. No approval is rendered off it. The runtime's own
 *     measured truth still is, because it is independent of the spec.
 *
 * THE TWO SEAMS ARE CLOSED, AND ONE THING IS STILL NOT ON THE WIRE
 * ================================================================
 * `items[].requirement` and `desired.profile_code`/`profile_version` landed in
 * contract v2 and are rendered below. The forcing test that held the seam open
 * was inverted rather than deleted, and now asserts they are present and
 * consumed.
 *
 * WHAT IS STILL NOT RENDERED, AND WHY. The owner describes a profile-level
 * rule: Demo Connections requested and EVERY attempted connection failed holds
 * the tenant below Ready, even when every member is optional. That rule is not
 * on this contract. `readiness_block.code` has exactly one value today —
 * `demo_profile_unreadable` — and nothing on the payload reports an
 * all-attempts-failed block. The only way to put it on the screen would be to
 * compute it here from `items[]`, which is deriving readiness on the frontend
 * from a list that is not the authority for it. So the screen renders
 * `readiness_block` and says nothing about that rule. Reported, not invented.
 *
 * THE FOUR-STAGE READINESS MODEL IS NOT RENDERED. SUPPORTED ->
 * SECRET_AVAILABLE -> PROVIDER_VERIFIED -> CLUVIK_E2E_VERIFIED is the canonical
 * model and no authoritative backend source emits it: it lives as a
 * hand-measured table in DEMO_CONNECTIONS_RECONCILIATION.md §7 and in no code
 * on either side of the wire. Deriving it from `outcome` would be a fabrication
 * with the shape of a measurement.
 */
import { describeEnvironment } from './environmentPresentation';

/* ── items[].outcome ─────────────────────────────────────────────────────
 *
 * `outcome` is the ONLY value a chip renders, and the contract says so. The
 * `tone` column is the load-bearing one: four of these seven are muted because
 * four of them are not measurements of anything. Widening one of those to
 * `warning` or `destructive` would make the screen report a failure nobody
 * observed, which is worse than the screen not existing.
 */
export const OUTCOME_PRESENTATION = {
  // THE WORD "CONNECTED" IS NOT USED ON THIS CARD. `outcome` is the profile's
  // credential probe verdict at attach (DEMO_PROFILE_TRUTH §4): it says the
  // credential worked when it was checked, and nothing about whether any
  // feature uses it — that is the separate `feature_enabled` axis under the
  // chip. "Connected" reads as both at once, which is exactly the merge the
  // two axes exist to prevent.
  connected: {
    label: 'Provider verified',
    tone: 'success',
    badge: 'demo_connected',
    meaning: 'The credential probe passed when this row was attached — the provider accepted the credential at that moment. Whether a feature uses it is the line below, not this chip.',
  },
  pending: {
    label: 'Not validated',
    tone: 'warning',
    badge: 'demo_pending',
    meaning: 'A credential is attached and its probe has not run. Not validated is not a pass.',
  },
  not_configured: {
    label: 'Not configured',
    tone: 'muted',
    badge: 'demo_not_configured',
    meaning: 'The runtime attached a row for this connection with no credential behind it.',
  },
  attention: {
    label: 'Validation failed',
    tone: 'destructive',
    badge: 'demo_attention',
    // The four Platform statuses the contract collapses into this one verdict.
    // Splitting them here would be a claim no measurement supports.
    meaning: 'The credential probe did not pass: the runtime reported this connection as degraded, expired, needing re-auth, or failed.',
  },
  unrecognised: {
    label: 'Status not recognised',
    tone: 'muted',
    badge: 'demo_unrecognised',
    meaning: 'The runtime reported a status this build cannot read. Passed through verbatim beside this chip, and not guessed upward.',
  },
  no_outcome_reported: {
    label: 'No outcome reported',
    tone: 'muted',
    badge: 'demo_no_outcome_reported',
    meaning: 'The runtime answered and said nothing about this connection. The approval expected it, and no outcome came back — which is neither a pass nor a failure.',
  },
  unknown: {
    label: 'Not established',
    tone: 'muted',
    badge: 'demo_unknown',
    meaning: 'The runtime could not be read, so this connection was not measured. Not established is not the same as failed.',
  },
};

/**
 * An outcome the contract does not carry. Reached only if the server grows a
 * value and this file is not updated — the case the shared picker got wrong by
 * having no such branch at all.
 *
 * Muted and explicitly unmapped. Never `unknown`: `unknown` is a contract value
 * with a specific meaning ("the runtime could not be read"), and quietly
 * relabelling an unheard-of value as one of the seven would hide exactly the
 * drift this branch exists to expose.
 */
const UNMAPPED_OUTCOME = {
  label: 'Outcome not recognised',
  tone: 'muted',
  badge: 'demo_unknown',
  meaning: 'The API returned an outcome this build does not know. It is shown as-is rather than mapped onto one that looks similar.',
};

export function describeOutcome(outcome) {
  const key = String(outcome ?? '');
  const found = OUTCOME_PRESENTATION[key];
  if (!found) return { ...UNMAPPED_OUTCOME, outcome: key || null, mapped: false };
  return { ...found, outcome: key, mapped: true };
}

/* ── items[].requirement ──────────────────────────────────────────────────
 *
 * WHAT THE APPROVAL RECORDED, never what the runtime says today, and the
 * three-valued domain is the whole point of the field.
 *
 * `null` IS NOT `optional`. Agent A chose an enum over a boolean precisely so
 * that absence keeps a third meaning, and the stored-data migration maps
 * absent -> optional at the BACKEND, where the decision is recorded and
 * auditable. If the wire says null, this build did not receive a decision, and
 * rendering "Optional" would put a decision on screen that nobody took.
 *
 * NOT A HEALTH AXIS. The pill is neutral in every case: `required` is not bad
 * news and `optional` is not good news — they are properties of a signed
 * approval. What differs is the CONSEQUENCE when a member is not working, and
 * that is what `consequence` below carries and what the render tones.
 */
export const REQUIREMENT_NOT_STATED = 'not_stated';

export const REQUIREMENT_PRESENTATION = {
  required: {
    label: 'Required',
    stated: true,
    // A RULE, NOT A VERDICT ABOUT THIS TENANT. Whether this tenant is below
    // Ready is `readiness_block`'s answer and nothing else's — see the module
    // header. This sentence says what the approval means, not what happened.
    meaning: 'The approval recorded this connection as required.',
    consequence: 'A required connection that is not working holds a tenant below Ready.',
    // Used only to tone the consequence line under a FAILING member.
    failingTone: 'destructive',
  },
  optional: {
    label: 'Optional',
    stated: true,
    meaning: 'The approval recorded this connection as optional.',
    consequence: 'A tenant can reach Ready with an optional connection not working; it shows as an integration warning.',
    failingTone: 'warning',
  },
  [REQUIREMENT_NOT_STATED]: {
    label: 'Not stated',
    stated: false,
    // The load-bearing sentence on this whole screen's newest field.
    meaning: 'The approval did not record whether this connection is required. Absent is not optional.',
    consequence: 'Because the approval is silent, what a failure here costs is not stated either.',
    failingTone: 'muted',
  },
};

/* ── items[].connection_configured / items[].feature_enabled ─────────────
 *
 * TWO FACTS THE PLATFORM NOW SERVES APART (contract v3, item 18). `outcome`
 * says whether the shared demo CREDENTIAL was accepted at the profile probe.
 * `feature_enabled` says whether the feature that consumes this connection is
 * switched on for THIS tenant, by that feature's own rule — Maps activation,
 * the WhatsApp channel toggle, the storage binding, the messaging default.
 * A Connected member whose feature is not enabled is the ordinary state for
 * four of the eleven, and it is an operator action, not a failure: it is
 * toned `warning`, never `destructive`.
 *
 * TRI-STATE, and `null` is the load-bearing value: it means the runtime did
 * not say — an older Platform build, or no answer at all. It is never
 * rendered as off, and never as on. `describeFeature` is the only way the
 * value reaches a render.
 */
export const FEATURE_PRESENTATION = {
  enabled: {
    label: 'Feature enabled',
    tone: 'success',
    meaning: 'The feature that uses this connection resolves it for this tenant right now.',
  },
  not_enabled: {
    label: 'Feature not enabled',
    tone: 'warning',
    meaning: 'A credential is attached, but the consuming feature is not switched on for this tenant. That is an operator action, not a failure.',
  },
  not_reported: {
    label: 'Feature state not reported',
    tone: 'muted',
    meaning: 'The runtime did not report whether the consuming feature is on. Not reported is neither on nor off.',
  },
};

export function describeFeature(enabled) {
  if (enabled === true) return { ...FEATURE_PRESENTATION.enabled, enabled: true, state: 'enabled' };
  if (enabled === false) return { ...FEATURE_PRESENTATION.not_enabled, enabled: false, state: 'not_enabled' };
  return { ...FEATURE_PRESENTATION.not_reported, enabled: null, state: 'not_reported' };
}

/** `true`/`false` verbatim from the wire; anything else is "not reported". */
export function describeConfigured(configured) {
  if (configured === true) return { configured: true, label: 'Configured' };
  if (configured === false) return { configured: false, label: 'Not configured' };
  return { configured: null, label: 'Configured state not reported' };
}

/**
 * One item's requirement. `null`/`undefined` resolve to the NOT-STATED entry —
 * never to `optional`.
 *
 * A value outside the domain resolves there too, flagged `mapped: false`: a
 * requirement word this build cannot read is not evidence that a member is
 * optional, which is the only direction this could be got wrong dangerously.
 */
export function describeRequirement(requirement) {
  if (requirement === null || requirement === undefined) {
    return { ...REQUIREMENT_PRESENTATION[REQUIREMENT_NOT_STATED],
             requirement: null, mapped: true };
  }
  const key = String(requirement);
  const found = REQUIREMENT_PRESENTATION[key];
  if (!found) {
    return {
      ...REQUIREMENT_PRESENTATION[REQUIREMENT_NOT_STATED],
      label: 'Requirement not recognised',
      meaning: 'The approval recorded a requirement value this build does not know. It is not read as optional.',
      requirement: key,
      mapped: false,
    };
  }
  return { ...found, requirement: key, mapped: true };
}

/* ── readiness_block ──────────────────────────────────────────────────────
 *
 * Non-null means the frozen intent itself is holding the tenant below Ready.
 * REPAIRABLE, NOT FAILED: the approval is intact, nothing was discarded, and
 * the block clears when the profile resolves.
 */
export const READINESS_BLOCK_PRESENTATION = {
  demo_profile_unreadable: {
    label: 'Held below Ready',
    tone: 'warning',
    title: 'Cluvik could not read what the approved profile promises',
    meaning: 'Add Demo Connections was approved and the profile could not be read at approval time, so what the tenant was promised was never established. The tenant is held below Ready until the profile resolves. The approval is intact and nothing has been discarded.',
  },
};

/**
 * The readiness block, or `null`.
 *
 * `null` HERE MEANS THIS GATE IS NOT BLOCKING — it does NOT mean the tenant is
 * Ready. Readiness has other gates (entitlement, runtime verification) that
 * this payload knows nothing about, so the render says "this gate is not
 * holding the tenant", never "the tenant is Ready".
 */
export function describeReadinessBlock(payload) {
  const block = payload?.readiness_block;
  if (!block) return null;
  const code = String(block.code ?? '');
  const known = READINESS_BLOCK_PRESENTATION[code];
  return {
    code: code || null,
    mapped: Boolean(known),
    label: known?.label || 'Held below Ready',
    tone: known?.tone || 'warning',
    title: known?.title || 'The approved Demo Connections profile is holding this tenant below Ready',
    // The server writes an operator-facing `detail`; it is the better sentence
    // because it names the profile. The static meaning is the fallback.
    meaning: block.detail || known?.meaning
      || 'This build does not know this block code. It is shown as-is rather than treated as cleared.',
    reason: describeReason(block.reason),
    reasonCode: block.reason ?? null,
    profileId: block.profile_id ?? null,
  };
}

/* ── the two profile identities, which answer different questions ─────────
 *
 * `profile_code` + `profile_version`  WHICH APPROVED DEFINITION this is.
 * `profile_fingerprint` + algorithm    ARE THESE EXACT FROZEN ITEMS UNCHANGED.
 *
 * Shown as two separate facts, never merged into one "profile identity" line.
 * They fail differently: a version moving is a definition that was revised on
 * purpose; a fingerprint changing under a fixed version is an approval that was
 * altered after signing.
 *
 * FINGERPRINTS ARE NEVER COMPARED ACROSS ALGORITHMS, and this UI never compares
 * fingerprints at all. `fingerprint_algorithm` was added because
 * `_DESIRED_ITEM_FIELDS` gained `requirement`, which changes every digest — a
 * SCHEMA CHANGE MUST NOT READ AS TAMPERING. The algorithm is rendered beside
 * the digest so a reader can see which definition of "unchanged" applies.
 */
export const FINGERPRINT_ALGORITHM_LABELS = {
  'sha256/desired-items-v1': 'sha256 over desired items (v1 — before requirements were recorded)',
  'sha256/desired-items-v2': 'sha256 over desired items (v2 — includes each member’s requirement)',
};

export function describeFingerprintAlgorithm(algorithm) {
  if (!algorithm) return null;
  return FINGERPRINT_ALGORITHM_LABELS[String(algorithm)]
    // Shown, never dropped: a digest with an unnamed algorithm is a digest
    // nothing may be concluded from, and a blank reads as "no algorithm".
    || `${algorithm} — this build does not know this algorithm, so nothing may be concluded from the digest`;
}

/** `profile_code v3`, or null when there is no definition identity to show. */
export function describeDefinition(code, version) {
  const hasCode = Boolean(code);
  const hasVersion = Number.isFinite(version);
  if (!hasCode && !hasVersion) return null;
  if (hasCode && hasVersion) return `${code} v${version}`;
  return hasCode ? String(code) : `v${version}`;
}

/* ── desired.state ───────────────────────────────────────────────────────
 *
 * NOT a health axis, and deliberately not green. "The approval is readable" is
 * a statement about a document, not about a connection, and a success tone here
 * would be read as "the connections are fine" from across the room.
 */
export const DESIRED_PRESENTATION = {
  recorded: {
    label: 'Recorded',
    tone: 'sky',
    badge: 'desired_recorded',
    meaning: 'The selection was frozen at approval and verified on this read.',
  },
  not_requested: {
    label: 'Not requested',
    tone: 'muted',
    badge: 'desired_not_requested',
    meaning: 'This tenant was approved without Demo Connections.',
  },
  unavailable_at_freeze: {
    label: 'Unavailable at approval',
    tone: 'warning',
    badge: 'desired_unavailable_at_freeze',
    meaning: 'Demo Connections were requested, and the profile could not be read at the moment of approval. The item list was never recorded, so there is nothing to compare against.',
  },
  not_recorded: {
    label: 'Not recorded',
    tone: 'muted',
    badge: 'desired_not_recorded',
    meaning: 'No frozen specification carries an intent for this tenant. Absence of a record is not evidence that nothing was requested.',
  },
  unverified: {
    label: 'Failed verification',
    tone: 'destructive',
    badge: 'desired_unverified',
    meaning: 'The frozen specification failed its tamper seal, or was frozen under a version this build cannot read. Nothing is read out of it — an approval shown off an unverified document is exactly what the seal exists to prevent.',
  },
};

/* ── attempted.state ─────────────────────────────────────────────────── */
export const ATTEMPTED_PRESENTATION = {
  not_recorded: {
    label: 'Not recorded',
    tone: 'muted',
    badge: 'attempted_not_recorded',
    meaning: 'No provisioning job row exists for this tenant. Tenants created by the older vendored path have none, and absence of a row is not evidence that nothing ran.',
  },
  in_progress: { label: 'In progress', tone: 'sky', badge: 'attempted_in_progress', meaning: 'A provisioning attempt is queued or running.' },
  completed: { label: 'Completed', tone: 'success', badge: 'attempted_completed', meaning: 'The provisioning attempt finished. What it achieved is the ACTUAL column, not this one.' },
  failed: { label: 'Failed', tone: 'destructive', badge: 'attempted_failed', meaning: 'The provisioning attempt failed or was cancelled.' },
  unrecognised: {
    label: 'Status not recognised',
    tone: 'muted',
    badge: 'attempted_unrecognised',
    meaning: 'The job carries a status this build cannot read. Shown verbatim beside this chip.',
  },
};

/* ── actual.state ────────────────────────────────────────────────────── */
export const ACTUAL_PRESENTATION = {
  reported: { label: 'Reported', tone: 'sky', badge: 'actual_reported', meaning: 'The environment runtime answered. The counts below are its answer.' },
  unavailable: {
    label: 'Could not be read',
    badge: 'actual_unavailable',
    // WARNING, NOT DESTRUCTIVE. Nothing failed; nothing was measured. The tone
    // has to be loud enough that an operator does not read the screen as a
    // pass, and quiet enough that it does not assert an outcome.
    tone: 'warning',
    meaning: 'The runtime did not answer, so nothing on this screen is a measurement of what is connected.',
  },
  not_requested: {
    label: 'Not requested',
    tone: 'muted',
    badge: 'actual_not_requested',
    meaning: 'This tenant never asked for Demo Connections, so the runtime was not read at all.',
  },
};

/* ── reason codes ────────────────────────────────────────────────────── */

/** actual.unavailable_reason + desired.unavailable_reason, in plain words. */
export const REASON_LABELS = {
  runtime_not_attached: 'the environment has no runtime attached',
  runtime_error: 'the runtime returned an error',
  runtime_unreachable: 'the runtime could not be reached',
  no_manifest: 'the runtime has never attached anything for this tenant',
  profile_not_found: 'the profile named in the approval does not exist',
  hash_mismatch: 'the frozen specification does not match its own seal',
  unsupported_version: 'the specification was frozen under a version this build cannot read',
  not_found: 'the specification could not be found',
  invalid_spec: 'the specification is not readable',
  secret_in_spec: 'the specification carries credential material and was refused',
};

/**
 * A reason code as words. An unheard-of code is de-underscored and shown, never
 * dropped: a blank where a reason should be reads as "no reason", which is the
 * opposite of what an unmapped code means.
 */
export function describeReason(code) {
  if (!code) return null;
  return REASON_LABELS[String(code)] || String(code).replace(/_/g, ' ');
}

/* ── counts ──────────────────────────────────────────────────────────── */

/**
 * THE ONLY WAY A COUNT REACHES THE RENDER.
 *
 * `null` in, `null` out — never `0`. `0` states "measured, and none"; `null`
 * states "not measured". The contract makes every actual count `null` when the
 * runtime could not be read, and a `?? 0` anywhere on the way to the screen
 * turns an unanswered question into a confident zero.
 */
export function formatCount(value) {
  return Number.isFinite(value) ? String(value) : null;
}

/**
 * The four actual counts, with the contract's own invariant enforced a second
 * time on this side of the wire.
 *
 * Defence in depth, not distrust: if a server regression ever sent a count
 * alongside `unavailable`, rendering it would be rendering a number nobody
 * measured, and the frontend is the last place that can refuse to.
 */
export function describeActualCounts(actual) {
  const measured = actual?.state === 'reported';
  const pick = (key) => (measured ? actual?.[key] : null);
  return {
    measured,
    reported: formatCount(pick('reported_count')),
    connected: formatCount(pick('connected_count')),
    attention: formatCount(pick('attention_count')),
    noOutcome: formatCount(pick('no_outcome_count')),
  };
}

/* ── profile drift ───────────────────────────────────────────────────── */

/**
 * `profile_drift` is a TRI-STATE and the middle value is the one that matters.
 *
 * `null` means one of the two profile ids is unknown, so the comparison was
 * never made. Rendering that as "no drift" — the shape a `!drift` check falls
 * into without anyone deciding to — asserts that the approved profile and the
 * attached profile agree, on the strength of not having looked.
 */
export function describeProfileDrift(payload) {
  return driftAxis(payload?.profile_drift, {
    driftedLabel: 'A different profile is attached',
    driftedDetail: 'The runtime attached a profile other than the one the operator approved.',
    matchLabel: 'Matches the approved profile',
    matchDetail: 'The attached profile is the one named in the approval.',
    unknownDetail: 'One of the two profile ids is unknown, so the approved profile and the attached profile were never compared. This is not a statement that they match.',
  });
}

/**
 * `profile_version_drift` — the SAME profile at a DIFFERENT version.
 *
 * Its own axis, because `profile_drift` cannot see it: both sides name
 * `cluvik-e2e-demo`, the ids match, and the definitions do not. The two call
 * for different operator actions — a different profile is a wrong selection, a
 * different version is a definition that moved — so folding them into one
 * field would make a `true` ambiguous.
 *
 * `null` is NOT MEASURABLE, exactly as its sibling: an approval frozen before
 * versions existed, or a runtime that did not report one.
 */
export function describeVersionDrift(payload) {
  return driftAxis(payload?.profile_version_drift, {
    driftedLabel: 'A different version of the same profile',
    driftedDetail: 'The definition moved after this tenant was approved. The tenant holds the version it was approved against.',
    matchLabel: 'The approved version is the attached one',
    matchDetail: 'The runtime is on the same version of this profile that the approval named.',
    unknownDetail: 'One of the two sides has no version, so the approved definition and the attached one were never compared. This is not a statement that they match.',
  });
}

/**
 * DRIFT IS INFORMATION, and the payload says so in a field.
 *
 * `drift_is_advisory` is the contract stating that neither drift field
 * authorises anything: repair re-applies the FROZEN version, and a runtime
 * whose profile has moved on refuses rather than applying the newer
 * definition. So this screen offers no "update to latest", no "sync", and no
 * control of any kind off a drift reading — a `true` here is a fact to act on
 * elsewhere, deliberately, not a button.
 */
export function describeDriftAdvisory(payload) {
  const advisory = payload?.drift_is_advisory;
  return {
    advisory: advisory !== false,
    note: advisory === false
      // Never seen today: the server sends `true`. If it ever sends false, the
      // honest render is that this build does not know what that authorises —
      // not a freshly enabled mutation control.
      ? 'The API says drift is not advisory here. This build has no action for that and offers none.'
      : 'Drift is information. Nothing here changes the runtime: repair re-applies the version this tenant was approved against, and a runtime whose profile has moved on refuses rather than applying the newer definition.',
  };
}

/** The shared three-valued shape. `null` in, "not comparable" out — always. */
function driftAxis(value, words) {
  if (value === true) {
    return { known: true, drifted: true, label: words.driftedLabel,
             tone: 'destructive', detail: words.driftedDetail };
  }
  if (value === false) {
    return { known: true, drifted: false, label: words.matchLabel,
             tone: 'success', detail: words.matchDetail };
  }
  return { known: false, drifted: null, label: 'Not comparable',
           tone: 'muted', detail: words.unknownDetail };
}

/* ── items ───────────────────────────────────────────────────────────── */

/**
 * One view row per SERVED item. The list is `payload.items` and nothing else.
 *
 * Not built from `desired`, not built from a constant, not padded out to a
 * profile's worth of names. If the API sends two items the screen shows two,
 * and if it sends none the screen says the API sent none.
 */
export function describeItems(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => {
    const outcome = describeOutcome(item?.outcome);
    // DESIRED-side, travelling beside the ACTUAL half and contributing nothing
    // to it. `null` on a member nobody approved and on an approval frozen
    // before requirements existed — and `null` is never read as `optional`.
    const requirement = describeRequirement(item?.requirement);
    const configured = describeConfigured(item?.connection_configured);
    const feature = describeFeature(item?.feature_enabled);
    return {
      connection: item?.connection ?? null,
      provider: item?.provider ?? null,
      // The served label, with the join key as the only fallback. A local name
      // table would be a second copy of a string the runtime owns.
      label: item?.label || item?.connection || item?.provider || 'Unnamed connection',
      desired: item?.desired === true,
      // Verbatim from the runtime manifest, or null. Shown beside the chip so
      // an unrecognised value is legible rather than merely classified.
      actualStatus: item?.actual_status ?? null,
      requirement: requirement.requirement,
      requirementLabel: requirement.label,
      requirementStated: requirement.stated,
      requirementMeaning: requirement.meaning,
      requirementConsequence: requirement.consequence,
      requirementMapped: requirement.mapped,
      // The tone for the consequence line, applied ONLY where the member is
      // actually failing. A requirement is not a health reading, and toning
      // the pill itself would make "required" look like bad news.
      requirementFailingTone: requirement.failingTone,
      outcome: outcome.outcome,
      outcomeLabel: outcome.label,
      outcomeTone: outcome.tone,
      outcomeMeaning: outcome.meaning,
      outcomeMapped: outcome.mapped,
      badgeStatus: outcome.badge,
      detail: item?.detail ?? null,
      // ITEM 18. Configured and enabled, each tri-state, each straight from
      // the wire. Neither is derived from `outcome` and `outcome` is not
      // derived from them.
      configured: configured.configured,
      configuredLabel: configured.label,
      featureEnabled: feature.enabled,
      featureState: feature.state,
      featureLabel: feature.label,
      featureTone: feature.tone,
      featureMeaning: feature.meaning,
      featureDetail: item?.feature_detail ?? null,
    };
  });
}

/* ── the whole view ──────────────────────────────────────────────────── */

const DESIRED_FALLBACK = {
  label: 'State not recognised',
  tone: 'muted',
  badge: 'unknown',
  meaning: 'The API reported a desired state this build does not know.',
};
const ATTEMPTED_FALLBACK = {
  label: 'State not recognised',
  tone: 'muted',
  badge: 'unknown',
  meaning: 'The API reported an attempted state this build does not know.',
};
const ACTUAL_FALLBACK = {
  label: 'State not recognised',
  tone: 'muted',
  badge: 'unknown',
  meaning: 'The API reported an actual state this build does not know.',
};

/**
 * Everything the Demo Connections view renders, from the reconciled payload.
 *
 * @param {object}  args
 * @param {boolean} args.loading  A request is in flight.
 * @param {string}  args.error    A failed request, already reduced to text.
 * @param {object}  args.payload  The API body, when one arrived.
 */
export function describeTenantDemoConnections({ loading, error, payload } = {}) {
  if (loading) {
    return {
      state: 'loading',
      severity: 'unknown',
      headline: 'Reading the environment runtime…',
      detail: null,
      environment: null,
      desired: null,
      attempted: null,
      actual: null,
      counts: null,
      drift: null,
      versionDrift: null,
      driftAdvisory: null,
      readinessBlock: null,
      requirementCounts: null,
      items: [],
      undesiredItems: [],
    };
  }
  if (error || !payload) {
    // A FAILED READ IS NOT AN EMPTY TENANT. Rendering "no connections" here
    // would hand an operator positive evidence for a thing nobody looked at.
    return {
      state: 'error',
      severity: 'unknown',
      headline: 'Demo Connections could not be read',
      detail: error ? String(error) : 'No response was received.',
      environment: null,
      desired: null,
      attempted: null,
      actual: null,
      counts: null,
      drift: null,
      versionDrift: null,
      driftAdvisory: null,
      // NOT a statement that nothing is blocking: the read failed, so this
      // gate's answer is unknown like everything else on the payload.
      readinessBlock: null,
      requirementCounts: null,
      items: [],
      undesiredItems: [],
    };
  }

  const env = describeEnvironment(payload.environment);
  const rawDesired = payload.desired || {};
  const rawAttempted = payload.attempted || {};
  const rawActual = payload.actual || {};

  const desiredP = DESIRED_PRESENTATION[String(rawDesired.state)] || DESIRED_FALLBACK;
  const attemptedP = ATTEMPTED_PRESENTATION[String(rawAttempted.state)] || ATTEMPTED_FALLBACK;
  const actualP = ACTUAL_PRESENTATION[String(rawActual.state)] || ACTUAL_FALLBACK;

  // THE APPROVAL IS SHOWN ONLY WHEN IT VERIFIED. `unverified` contributes no
  // profile id, no label and no count — the server already withholds them, and
  // this flag is what stops the render from filling the space back in from
  // somewhere else.
  const showApproval = rawDesired.state === 'recorded';

  const desired = {
    state: rawDesired.state ?? null,
    label: desiredP.label,
    tone: desiredP.tone,
    badge: desiredP.badge,
    meaning: desiredP.meaning,
    mapped: Boolean(DESIRED_PRESENTATION[String(rawDesired.state)]),
    showApproval,
    profileId: showApproval ? (rawDesired.profile_id ?? null) : null,
    profileLabel: showApproval ? (rawDesired.profile_label ?? null) : null,
    profileKind: showApproval ? (rawDesired.profile_kind ?? null) : null,
    profileFingerprint: showApproval ? (rawDesired.profile_fingerprint ?? null) : null,
    // TWO IDENTITIES, TWO QUESTIONS. `definition` is WHICH approved definition
    // this is; the fingerprint is whether these exact frozen items are
    // unchanged. Kept as separate fields so no render can merge them.
    profileCode: showApproval ? (rawDesired.profile_code ?? null) : null,
    profileVersion: showApproval && Number.isFinite(rawDesired.profile_version)
      ? rawDesired.profile_version : null,
    definition: showApproval
      ? describeDefinition(rawDesired.profile_code, rawDesired.profile_version) : null,
    // Rendered beside the digest: a schema change must not read as tampering,
    // and a reader has to be able to see which definition of "unchanged" the
    // digest was taken under.
    fingerprintAlgorithm: showApproval
      ? describeFingerprintAlgorithm(rawDesired.fingerprint_algorithm) : null,
    fingerprintAlgorithmCode: showApproval ? (rawDesired.fingerprint_algorithm ?? null) : null,
    profileUpdatedAt: showApproval ? (rawDesired.profile_updated_at ?? null) : null,
    observedAt: showApproval ? (rawDesired.observed_at ?? null) : null,
    // NULL UNLESS RECORDED. A count is a claim, and there is nothing to claim
    // in the other four states.
    itemCount: showApproval ? formatCount(rawDesired.item_count) : null,
    reason: describeReason(rawDesired.unavailable_reason),
    reasonCode: rawDesired.unavailable_reason ?? null,
    specId: rawDesired.spec_id ?? null,
    specVersion: rawDesired.spec_version_as_frozen ?? null,
    frozenAt: rawDesired.frozen_at ?? null,
    intentSchemaVersion: rawDesired.intent_schema_version ?? null,
  };

  const attempted = {
    state: rawAttempted.state ?? null,
    label: attemptedP.label,
    tone: attemptedP.tone,
    badge: attemptedP.badge,
    meaning: attemptedP.meaning,
    mapped: Boolean(ATTEMPTED_PRESENTATION[String(rawAttempted.state)]),
    jobId: rawAttempted.job_id ?? null,
    jobStatus: rawAttempted.job_status ?? null,
    // Attempts on THIS row. A fresh budget is a new row, never a rewritten
    // field, so this is not attempts-ever and is not labelled as though it is.
    retryCount: formatCount(rawAttempted.retry_count),
    maxRetries: formatCount(rawAttempted.max_retries),
    lastError: rawAttempted.last_error ?? null,
    createdAt: rawAttempted.created_at ?? null,
    updatedAt: rawAttempted.updated_at ?? null,
  };

  const counts = describeActualCounts(rawActual);
  const actual = {
    state: rawActual.state ?? null,
    label: actualP.label,
    tone: actualP.tone,
    badge: actualP.badge,
    meaning: actualP.meaning,
    mapped: Boolean(ACTUAL_PRESENTATION[String(rawActual.state)]),
    measured: counts.measured,
    reason: describeReason(rawActual.unavailable_reason),
    reasonCode: rawActual.unavailable_reason ?? null,
    detail: rawActual.detail ?? null,
    profileId: rawActual.profile_id ?? null,
    profileCode: rawActual.profile_code ?? null,
    profileVersion: Number.isFinite(rawActual.profile_version) ? rawActual.profile_version : null,
    definition: describeDefinition(rawActual.profile_code, rawActual.profile_version),
    attachedAt: rawActual.attached_at ?? null,
  };

  const items = describeItems(payload);

  const view = {
    state: 'ready',
    tenantId: payload.tenant_id ?? null,
    environment: env,
    desired,
    attempted,
    actual,
    counts,
    drift: describeProfileDrift(payload),
    versionDrift: describeVersionDrift(payload),
    driftAdvisory: describeDriftAdvisory(payload),
    readinessBlock: describeReadinessBlock(payload),
    // Approved and reconciled.
    items: items.filter((i) => i.desired),
    // A breakdown of the APPROVED members by what the approval said about
    // them. Counted over the served list, never over a constant, and `null`
    // when there is no readable approval to count.
    requirementCounts: showApproval ? countRequirements(items.filter((i) => i.desired)) : null,
    // Attached and never approved. Surfaced as its own group rather than mixed
    // in: a connection the tenant has that nobody asked for is a different
    // question from one that was asked for and did not arrive.
    undesiredItems: items.filter((i) => !i.desired),
  };
  return { ...view, ...summarize(view) };
}

/**
 * The approved members, by what the approval recorded about each.
 *
 * `notStated` is its own bucket and is never added to `optional`. That is the
 * whole reason the field is an enum: a screen that shows "3 optional" when the
 * approval stated nothing about any of them has invented three decisions.
 */
export function countRequirements(items) {
  const list = items || [];
  return {
    total: list.length,
    required: list.filter((i) => i.requirement === 'required').length,
    optional: list.filter((i) => i.requirement === 'optional').length,
    notStated: list.filter((i) => i.requirement === null).length,
  };
}

/**
 * The one headline, at the severity that was actually measured.
 *
 * `unknown` is a tier, not a shade of `warning`. Collapsing "we could not look"
 * into either "fine" or "broken" is the whole failure this screen exists to
 * make impossible.
 */
function summarize(view) {
  const { actual, desired, counts } = view;

  if (desired.state === 'unverified') {
    return {
      severity: 'blocked',
      headline: 'The approval record failed verification',
      detail: `${desired.meaning}${desired.reason ? ` Reason: ${desired.reason}.` : ''} What the runtime reports is shown below regardless — it is measured independently of the specification.`,
    };
  }
  if (view.readinessBlock) {
    // The frozen intent itself is holding the tenant below Ready. Ranked above
    // every runtime reading because it is a statement about the tenant's
    // lifecycle rather than about one connection — and REPAIRABLE, which the
    // words have to carry: the approval is intact and the block clears when
    // the profile resolves.
    //
    // Mutually exclusive with `unverified` by construction: the server derives
    // the block from the VERIFIED intent, so a spec that failed its seal
    // contributes no block either.
    return {
      severity: 'blocked',
      headline: view.readinessBlock.title,
      detail: view.readinessBlock.meaning,
    };
  }
  if (actual.state === 'not_requested') {
    return {
      severity: 'ok',
      headline: 'Demo Connections were not requested for this tenant',
      detail: 'No shared provider credentials were attached, and the runtime was not read.',
    };
  }
  if (actual.state === 'unavailable') {
    return {
      severity: 'unknown',
      headline: `The ${view.environment.label} runtime could not be read`,
      detail: `Nothing below is a measurement of what is connected${actual.reason ? ` — ${actual.reason}` : ''}. Every connection reads "not established", which is not the same as failed.`,
    };
  }
  if (!actual.measured) {
    return {
      severity: 'unknown',
      headline: 'The runtime state could not be established',
      detail: actual.meaning,
    };
  }

  // THE OTHER DIRECTION OF THE SAME INVARIANT.
  //
  // `describeActualCounts` defends against a count arriving alongside a state
  // that measured nothing. This is the mirror: a `reported` state whose counts
  // are ABSENT. `formatCount` correctly returns `null` for one — null in, null
  // out, never 0 — and every guard below is `!== '0'`, which `null` satisfies.
  // So the headline template interpolated it, and an operator was shown, in
  // the one sentence this card exists to say:
  //
  //     "null connected, null needing attention, null with no outcome reported"
  //
  // Found by rendering the state rather than by reading the module: every
  // count is handled correctly right up to the string that prints it.
  //
  // The fix is not `?? 0`. A missing count under `reported` means the server
  // said it measured and then did not say what it measured, and that is the
  // same fact as not having measured — which this screen already has a tier
  // for, and it is not `warning` and not `ok`.
  const missing = ['reported', 'connected', 'attention', 'noOutcome']
    .filter((k) => counts[k] === null);
  if (missing.length) {
    return {
      severity: 'unknown',
      headline: 'The runtime answered without saying what it found',
      detail: `The ${view.environment.label} runtime reported its state but did not return `
        + `${missing.length === 4 ? 'any of its counts' : `its ${missing.join(', ')} count${missing.length === 1 ? '' : 's'}`}. `
        + 'Nothing below is a count of what is connected.',
    };
  }

  // Measured. A ZERO IS ALLOWED HERE and nowhere else on this screen: the
  // runtime answered, and "nothing attached" is its answer.
  if (counts.reported === '0') {
    return {
      severity: 'warning',
      headline: 'The runtime answered, and has nothing attached for this tenant',
      detail: desired.state === 'recorded' && desired.itemCount && desired.itemCount !== '0'
        ? `${desired.itemCount} connection${desired.itemCount === '1' ? ' was' : 's were'} approved and none is attached.`
        : 'No connection rows exist for this tenant in its environment runtime.',
    };
  }

  const parts = [];
  if (counts.connected !== '0') parts.push(`${counts.connected} connected`);
  if (counts.attention !== '0') parts.push(`${counts.attention} needing attention`);
  if (counts.noOutcome !== '0') parts.push(`${counts.noOutcome} with no outcome reported`);
  const severity = counts.attention !== '0' ? 'warning'
    : (counts.noOutcome !== '0' || view.undesiredItems.length ? 'warning' : 'ok');

  return {
    severity,
    headline: parts.length ? parts.join(', ') : `${counts.reported} connections reported`,
    // Counted on the outcome axis, never as a score: "3 of 5" reads as a mark
    // out of the approval, and a connection nobody configured did not fail.
    detail: `${counts.reported} connection${counts.reported === '1' ? '' : 's'} reported by the ${view.environment.label} runtime.`,
  };
}
